import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { goalsService, type CreateWeightEntryRequest, type WeightEntry } from '@/services/goalsService';

export type { WeightEntry, CreateWeightEntryRequest };
import { formatDateInput } from '@/utils/dateTime';

const sortEntriesAscending = (entries: WeightEntry[]): WeightEntry[] =>
  [...entries].sort((a, b) => {
    const byDate = a.localDate.localeCompare(b.localDate);
    if (byDate !== 0) {
      return byDate;
    }

    const byMeasuredAt = a.measuredAtUtc.localeCompare(b.measuredAtUtc);
    if (byMeasuredAt !== 0) {
      return byMeasuredAt;
    }

    return a.id.localeCompare(b.id);
  });

const getLastNDaysRange = (days: number): { from: string; to: string } => {
  const to = formatDateInput();
  const fromDate = new Date();
  fromDate.setDate(fromDate.getDate() - (days - 1));
  return {
    from: formatDateInput(fromDate),
    to,
  };
};

export const useWeightStore = defineStore('weight', () => {
  const entries = ref<WeightEntry[]>([]);
  const isLoading = ref(false);
  const isSaving = ref(false);
  const hasLoaded = ref(false);
  const errorMessage = ref('');

  let loadPromise: Promise<WeightEntry[]> | null = null;

  const entriesAscending = computed(() => sortEntriesAscending(entries.value));
  const entriesNewestFirst = computed(() => [...entriesAscending.value].reverse());
  const latestEntry = computed(() => entriesAscending.value.at(-1) ?? null);

  const getWeightForDay = (day = formatDateInput()): WeightEntry | null => {
    for (let index = entriesAscending.value.length - 1; index >= 0; index -= 1) {
      const entry = entriesAscending.value[index];
      if (entry.localDate <= day) {
        return entry;
      }
    }

    return null;
  };

  const getEntriesInRange = (from?: string, to?: string): WeightEntry[] =>
    entriesAscending.value.filter((entry) => {
      if (from && entry.localDate < from) {
        return false;
      }
      if (to && entry.localDate > to) {
        return false;
      }
      return true;
    });

  const last7DaysEntries = computed(() => {
    const { from, to } = getLastNDaysRange(7);
    return getEntriesInRange(from, to);
  });

  const upsertEntry = (entry: WeightEntry): void => {
    const existingIndex = entries.value.findIndex((candidate) => candidate.id === entry.id);
    if (existingIndex === -1) {
      entries.value = [...entries.value, entry];
      return;
    }

    entries.value = entries.value.map((candidate, index) =>
      index === existingIndex ? entry : candidate,
    );
  };

  const removeEntryFromState = (id: string): void => {
    entries.value = entries.value.filter((entry) => entry.id !== id);
  };

  const loadEntries = async (): Promise<WeightEntry[]> => {
    if (loadPromise) {
      return loadPromise;
    }

    loadPromise = (async () => {
      isLoading.value = true;
      errorMessage.value = '';

      try {
        const loadedEntries = await goalsService.getWeightHistory();
        entries.value = loadedEntries;
        hasLoaded.value = true;
        return loadedEntries;
      } catch (error) {
        errorMessage.value = error instanceof Error ? error.message : 'Unable to load weight entries.';
        throw error;
      } finally {
        isLoading.value = false;
        loadPromise = null;
      }
    })();

    return loadPromise;
  };

  const ensureLoaded = async (): Promise<WeightEntry[]> => {
    return loadEntries();
  };

  const createEntry = async (entry: CreateWeightEntryRequest): Promise<WeightEntry> => {
    isSaving.value = true;
    errorMessage.value = '';

    try {
      const savedEntry = await goalsService.createWeightEntry(entry);
      upsertEntry(savedEntry);
      hasLoaded.value = true;
      return savedEntry;
    } catch (error) {
      errorMessage.value = error instanceof Error ? error.message : 'Unable to save weight entry.';
      throw error;
    } finally {
      isSaving.value = false;
    }
  };

  const updateEntry = async (
    id: string,
    updates: Partial<CreateWeightEntryRequest>,
  ): Promise<WeightEntry | null> => {
    isSaving.value = true;
    errorMessage.value = '';

    try {
      const updatedEntry = await goalsService.updateWeightEntry(id, updates);
      if (updatedEntry) {
        upsertEntry(updatedEntry);
        hasLoaded.value = true;
      }
      return updatedEntry;
    } catch (error) {
      errorMessage.value = error instanceof Error ? error.message : 'Unable to save weight entry.';
      throw error;
    } finally {
      isSaving.value = false;
    }
  };

  const deleteEntry = async (id: string): Promise<boolean> => {
    isSaving.value = true;
    errorMessage.value = '';

    try {
      const removed = await goalsService.deleteWeightEntry(id);
      if (removed) {
        removeEntryFromState(id);
        hasLoaded.value = true;
      }
      return removed;
    } catch (error) {
      errorMessage.value = error instanceof Error ? error.message : 'Unable to delete weight entry.';
      throw error;
    } finally {
      isSaving.value = false;
    }
  };

  return {
    entries,
    entriesAscending,
    entriesNewestFirst,
    latestEntry,
    last7DaysEntries,
    isLoading,
    isSaving,
    hasLoaded,
    errorMessage,
    getWeightForDay,
    getEntriesInRange,
    loadEntries,
    ensureLoaded,
    createEntry,
    updateEntry,
    deleteEntry,
  };
});
